import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { accountKeys } from "./useAccounts";
import { activityKeys } from "./useActivities";
import { contactKeys } from "./useContacts";
import { usersKeys } from "./useUsers";

/**
 * Caches whose contents are derived from a live integration.
 *
 * `AccountDto.source` and `PublicUser.source` are computed server-side from the
 * current connection health, and hard disconnects delete the synced records
 * outright, so connecting or disconnecting an integration makes all of these
 * stale at once.
 */
function integrationDerivedQueryKeys(): readonly (readonly unknown[])[] {
  // Built lazily: `src/hooks/queries/index.ts` re-exports this module and the
  // query modules import each other through that barrel, so reading the key
  // factories at module-eval time can observe a half-initialised module.
  return [
    accountKeys.all,
    usersKeys.all,
    contactKeys.all,
    activityKeys.all,
    ["salesforce-sync-status"],
  ];
}

/**
 * Invalidates every cache that depends on an integration being connected.
 *
 * `router.push()` and `router.refresh()` are not substitutes: they navigate or
 * re-render Server Components, but leave the React Query cache intact, so the
 * old `source` keeps rendering until the cache goes stale on its own.
 */
export async function invalidateIntegrationDerivedQueries(
  queryClient: QueryClient
): Promise<void> {
  await Promise.all(
    integrationDerivedQueryKeys().map((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    )
  );
}

/**
 * Returns a callback to run in the success path of any integration connect or
 * disconnect flow, so every surface reading integration-derived data refreshes
 * without a manual page reload.
 */
export function useIntegrationCacheInvalidation() {
  const queryClient = useQueryClient();

  return useCallback(
    () => invalidateIntegrationDerivedQueries(queryClient),
    [queryClient]
  );
}
